import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Formik } from "formik";
import * as Yup from "yup";
import AnimatedLoader from "../../../ui/animatedLoader";
import { deleteGroupOfGroupOrderApi, editGroupOfGroupOrderApi, getGroupsOfGroupOrderApi, saveGroupOfGroupOrderApi } from "../../../../shared/services/Api/Report";

const ModalGroupSetting = ({ show, onHide, user_id }) => {
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState("list"); // 'list' or 'form'
    const [groupData, setGroupsData] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [formError, setFormError] = useState("");
    const initialSelected = { name: "Select an option" }
    const [selected, setSelected] = useState(initialSelected);

    // Validation schema
    const validationSchema = Yup.object().shape({
        name: Yup.string()
            .trim()
            .required("Please enter a group title."),
        permitted_emails: Yup.array()
            .min(1, "Please add at least one email address.")
            .required("Email list is required"),
        email: Yup.string().test(
            "email-validation",
            "Invalid email format",
            function (value) {
                if (!value) return true; // Empty is valid for this field
                return validateEmailsInput(value) === "";
            }
        ),
    });

    const validateEmail = (email) => {
        if (!email) {
            return "Please enter your email address.";
        } else if (!email.includes("@")) {
            return "Missing '@' symbol in email address.";
        }
        const [localPart, domain] = email.split("@");
        if (!localPart) {
            return "Missing username before '@'.";
        }
        if (!domain) {
            return "Missing domain after '@'.";
        }
        if (!domain.includes(".")) {
            return "Domain must contain a dot (e.g., gmail.com).";
        }
        const domainParts = domain.split(".");
        if (domainParts.some((part) => part.trim() === "")) {
            return "Invalid domain structure.";
        }
        const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!regex.test(email)) {
            return "Invalid email format.";
        }
        return "";
    };

    const parseEmails = (value) => {
        return value
            .split(",")
            .map((item) => item.trim())
            .filter((item) => item !== "");
    };

    const validateEmailsInput = (value) => {
        const emails = parseEmails(value);
        for (let i = 0; i < emails.length; i += 1) {
            const singleEmail = emails[i];
            const error = validateEmail(singleEmail);
            if (error) {
                return `${error} ("${singleEmail}")`;
            }
        }
        return "";
    };

    useEffect(() => {
        if (user_id && show) {
            fetchEmailGroups();
        }
    }, [user_id, show]);

    const fetchEmailGroups = async () => {
        setLoading(true);
        try {
            const response = await getGroupsOfGroupOrderApi({ user_id });
            if (response) {
                setGroupsData(response);
            }
        } catch (error) {
            console.error("Failed to fetch groups", error);
            setFormError("Failed to load groups.");
        } finally {
            setLoading(false);
        }
    };

    const handleAddNew = () => {
        setSelectedGroup(null);
        setFormError("");
        setSelected(initialSelected);
        setView("form");
    };

    const handleEdit = (group) => {
        setSelectedGroup(group);
        setSelected({
            name: group?.name,
            id: group?.id
        })
        setFormError("");
        setView("form");
    };

    const handleBackToList = () => {
        setView("list");
        setSelectedGroup(null);
        setSelected(initialSelected);
        setFormError("");
    };

    const handleDelete = async (group_order_id) => {
        if (!window.confirm("Are you sure you want to delete this group?")) {
            return;
        }

        try {
            setLoading(true);
            const response = await deleteGroupOfGroupOrderApi({ group_order_id, user_id });
            if (response) {
                setGroupsData((prevState) => prevState.filter((item) => item.id !== group_order_id))
            }
        } catch (error) {
            setFormError("Failed to delete group.");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        handleBackToList();
        onHide();
    };

    const handleFormSubmit = async ({ email, ...values }) => {
        try {
            setFormError("");
            setLoading(true);
            const body = {
                group: {
                    ...values,
                },
                group_order_id: selected?.id,
                user_id
            }
            if (selectedGroup) {
                const response = await editGroupOfGroupOrderApi(body);
                if (response) {
                    setGroupsData((prevState) => prevState.map((item) => {
                        if (item.id === response.id) {
                            return response
                        }
                        return item
                    }));
                    setView('list')
                }
            } else {
                const response = await saveGroupOfGroupOrderApi(body);
                if (response) {
                    setGroupsData((prevState) => [response, ...prevState]);
                    setView('list')
                }
            }
        } catch (error) {
            setFormError(
                error?.response?.data?.message || "Failed to save group."
            );
        } finally {
            setLoading(false);
        }
    };

    const initialFormValues = {
        name: selectedGroup?.name || "",
        email: "",
        permitted_emails: selectedGroup?.permitted_emails || [],
    };

    return (
        <>
            <AnimatedLoader isLoading={loading} />
            <Modal show={show} size="lg" centered>
                <Modal.Body>
                    <div className="modal__wrapper">
                        <div className="inner__modal">
                            <div className="team__modal edit-profile__modal">
                                <button onClick={handleClose} type="button">
                                    <img src="/img/closemodal.svg" alt="close modal" />
                                </button>

                                <div className="modal__info">
                                    {formError && <div className="form__error">{formError}</div>}

                                    {/* LIST VIEW */}
                                    {view === "list" && (
                                        <div className="modal__info--main">
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    marginBottom: "20px",
                                                    width: "100%",
                                                }}
                                            >
                                                <h2>Groups</h2>
                                                <button
                                                    className="btn btn-success"
                                                    type="button"
                                                    onClick={handleAddNew}
                                                    disabled={loading}
                                                    style={{
                                                        padding: "10px 20px",
                                                        fontSize: "14px",
                                                        fontWeight: "600",
                                                        borderRadius: "6px",
                                                        border: "none",
                                                        cursor: loading ? "not-allowed" : "pointer",
                                                    }}
                                                >
                                                    <span style={{ marginRight: "6px" }}>+</span>
                                                    Add New Group
                                                </button>
                                            </div>

                                            {groupData?.length === 0 ? (
                                                <div
                                                    style={{
                                                        textAlign: "center",
                                                        padding: "40px 20px",
                                                        color: "#666",
                                                    }}
                                                >
                                                    <p>No groups found.</p>
                                                    <p style={{ fontSize: "14px" }}>
                                                        Click "Add New Group" to create one.
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="email-groups-list w-100">
                                                    {groupData?.map((group) => (
                                                        <div
                                                            key={group.id}
                                                            className="email-group-item"
                                                            style={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "center",
                                                                padding: "16px",
                                                                border: "1px solid #e0e0e0",
                                                                borderRadius: "8px",
                                                                marginBottom: "12px",
                                                                backgroundColor: "#f9f9f9",
                                                            }}
                                                        >
                                                            <div style={{ flex: 1, paddingRight: "16px" }}>
                                                                <h4
                                                                    style={{
                                                                        margin: "0 0 8px 0",
                                                                        fontSize: "16px",
                                                                        fontWeight: "600",
                                                                    }}
                                                                >
                                                                    {group?.name}
                                                                </h4>
                                                                <p
                                                                    style={{
                                                                        margin: 0,
                                                                        fontSize: "13px",
                                                                        color: "#666",
                                                                    }}
                                                                >
                                                                    {group?.permitted_emails?.length} email(s)
                                                                </p>
                                                            </div>
                                                            <div style={{ display: "flex", gap: "8px" }}>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleEdit(group)}
                                                                    disabled={loading}
                                                                    style={{
                                                                        padding: "8px 16px",
                                                                        backgroundColor: "#007bff",
                                                                        color: "white",
                                                                        border: "none",
                                                                        borderRadius: "6px",
                                                                        cursor: loading ? "not-allowed" : "pointer",
                                                                        fontSize: "14px",
                                                                        fontWeight: "500",
                                                                    }}
                                                                >
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleDelete(group.id)}
                                                                    disabled={loading}
                                                                    style={{
                                                                        padding: "8px 16px",
                                                                        backgroundColor: "#dc3545",
                                                                        color: "white",
                                                                        border: "none",
                                                                        borderRadius: "6px",
                                                                        cursor: loading ? "not-allowed" : "pointer",
                                                                        fontSize: "14px",
                                                                        fontWeight: "500",
                                                                    }}
                                                                >
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* FORM VIEW */}
                                    {view === "form" && (
                                        <Formik
                                            initialValues={initialFormValues}
                                            validationSchema={validationSchema}
                                            onSubmit={handleFormSubmit}
                                            enableReinitialize
                                        >
                                            {({
                                                values,
                                                errors,
                                                touched,
                                                handleChange,
                                                handleBlur,
                                                handleSubmit,
                                                setFieldValue,
                                                setFieldTouched,
                                            }) => {
                                                const validEmailError =
                                                    values.email && validateEmailsInput(values.email);

                                                const handleAddEmail = () => {
                                                    if (!values.email.trim()) return;

                                                    const error = validateEmailsInput(values.email);
                                                    if (error) {
                                                        setFormError(error);
                                                        return;
                                                    }

                                                    const newEmails = parseEmails(values.email);
                                                    setFieldValue("permitted_emails", [
                                                        ...values.permitted_emails,
                                                        ...newEmails,
                                                    ]);
                                                    setFieldValue("email", "");
                                                    setFormError("");
                                                };

                                                const handleRemoveEmail = (removeEmail) => {
                                                    setFieldValue(
                                                        "permitted_emails",
                                                        values.permitted_emails.filter((e) => e !== removeEmail)
                                                    );
                                                };

                                                return (
                                                    <div className="modal__info--main">
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                marginBottom: "20px",
                                                            }}
                                                        >
                                                            <button
                                                                type="button"
                                                                onClick={handleBackToList}
                                                                disabled={loading}
                                                                style={{
                                                                    background: "none",
                                                                    border: "none",
                                                                    fontSize: "24px",
                                                                    cursor: loading ? "not-allowed" : "pointer",
                                                                    marginRight: "12px",
                                                                    padding: "0",
                                                                    lineHeight: "1",
                                                                }}
                                                            >
                                                                ←
                                                            </button>
                                                            <h2 style={{ margin: 0 }}>
                                                                {selectedGroup
                                                                    ? "Edit Email Group"
                                                                    : "Add Email Group"}
                                                            </h2>
                                                        </div>

                                                        <div className="modal-setting w-100 p-0">
                                                            {/* Group Title */}
                                                            <div
                                                                className="modal-setting__input-section"
                                                                style={{ marginBottom: "24px" }}
                                                            >
                                                                <label className="modal-setting__emails-label">
                                                                    Group Title
                                                                </label>
                                                                <input
                                                                    className="modal-setting__emails-input"
                                                                    type="text"
                                                                    name="name"
                                                                    value={values.name}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    placeholder="Enter group title (e.g., Marketing Team)"
                                                                    disabled={loading}
                                                                />
                                                                {errors.name && touched.name && (
                                                                    <div
                                                                        className="form__error"
                                                                        style={{ marginTop: "8px" }}
                                                                    >
                                                                        {errors.name}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {/* <div className="modal-setting__input-section" style={{ position: "relative", width: "100%" }}>
                                                                <label className="modal-setting__emails-label">
                                                                    Corporate Order
                                                                </label>
                                                                <div
                                                                    style={{
                                                                        border: "1px solid #ccc",
                                                                        padding: "10px",
                                                                        cursor: "pointer",
                                                                        borderRadius: "4px",
                                                                        background: "#fff"
                                                                    }}
                                                                    onClick={() => setOpen(!open)}
                                                                >
                                                                    {selected?.name}
                                                                </div>

                                                                {open && (
                                                                    <div
                                                                        style={{
                                                                            border: "1px solid #ccc",
                                                                            borderTop: "none",
                                                                            position: "absolute",
                                                                            width: "100%",
                                                                            background: "#fff",
                                                                            borderRadius: "0 0 4px 4px",
                                                                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                                                            zIndex: "10",
                                                                        }}
                                                                    >
                                                                        {filtered.map((option) => (
                                                                            <div
                                                                                key={option.id}
                                                                                style={{ padding: "10px", cursor: "pointer" }}
                                                                                onClick={() => handleSelect(option)}
                                                                            >
                                                                                {option.name}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div> */}
                                                            {/* Email List */}
                                                            <div className="modal-setting__input-section">
                                                                <label className="modal-setting__emails-label">
                                                                    Email Addresses
                                                                </label>

                                                                <div
                                                                    className="modal-setting__email-list-section mb-4"
                                                                    style={{
                                                                        minHeight:
                                                                            values.permitted_emails.length > 0 ? "auto" : "0",
                                                                        marginBottom:
                                                                            values.permitted_emails.length > 0 ? "12px" : "0",
                                                                        padding:
                                                                            values.permitted_emails.length > 0 ? "12px" : "0",
                                                                    }}
                                                                >
                                                                    {values.permitted_emails.map((email, index) => (
                                                                        <span
                                                                            className="modal-setting__email-chip"
                                                                            key={`${email}-${index}`}
                                                                        >
                                                                            {email}
                                                                            <button
                                                                                type="button"
                                                                                className="modal-setting__email-remove"
                                                                                onClick={() => handleRemoveEmail(email)}
                                                                                disabled={loading}
                                                                            >
                                                                                &times;
                                                                            </button>
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                                <div style={{ display: "flex", gap: "8px" }}>
                                                                    <input
                                                                        className="modal-setting__emails-input"
                                                                        type="text"
                                                                        name="email"
                                                                        value={values.email}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                        onKeyPress={(e) => {
                                                                            if (e.key === "Enter") {
                                                                                e.preventDefault();
                                                                                handleAddEmail();
                                                                            }
                                                                        }}
                                                                        placeholder="Enter email address (comma-separated for multiple)"
                                                                        disabled={loading}
                                                                        style={{ flex: 1 }}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        onClick={handleAddEmail}
                                                                        disabled={
                                                                            loading ||
                                                                            !values.email.trim() ||
                                                                            validEmailError !== ""
                                                                        }
                                                                        style={{
                                                                            padding: "8px 16px",
                                                                            backgroundColor: "#28a745",
                                                                            color: "white",
                                                                            border: "none",
                                                                            borderRadius: "6px",
                                                                            cursor:
                                                                                loading ||
                                                                                    !values.email.trim() ||
                                                                                    validEmailError
                                                                                    ? "not-allowed"
                                                                                    : "pointer",
                                                                            fontSize: "14px",
                                                                            fontWeight: "500",
                                                                            whiteSpace: "nowrap",
                                                                        }}
                                                                    >
                                                                        Add Email
                                                                    </button>
                                                                </div>

                                                                {validEmailError && (
                                                                    <div
                                                                        className="form__error"
                                                                        style={{ marginTop: "8px" }}
                                                                    >
                                                                        {validEmailError}
                                                                    </div>
                                                                )}

                                                                {errors.permitted_emails && touched.permitted_emails && (
                                                                    <div
                                                                        className="form__error"
                                                                        style={{ marginTop: "8px" }}
                                                                    >
                                                                        {errors.permitted_emails}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Action Buttons */}
                                                            <div
                                                                style={{
                                                                    display: "flex",
                                                                    gap: "12px",
                                                                    marginTop: "24px",
                                                                }}
                                                            >
                                                                <button
                                                                    className="modal-setting__save-btn"
                                                                    type="button"
                                                                    onClick={handleBackToList}
                                                                    disabled={loading}
                                                                    style={{
                                                                        flex: 1,
                                                                        backgroundColor: "#6c757d",
                                                                        cursor: loading ? "not-allowed" : "pointer",
                                                                    }}
                                                                >
                                                                    Cancel
                                                                </button>
                                                                <button
                                                                    className="modal-setting__save-btn"
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setFieldTouched("name", true);
                                                                        setFieldTouched("permitted_emails", true);
                                                                        handleSubmit();
                                                                    }}
                                                                    disabled={loading}
                                                                    style={{
                                                                        flex: 1,
                                                                        cursor: loading ? "not-allowed" : "pointer",
                                                                    }}
                                                                >
                                                                    {selectedGroup ? "Update Group" : "Save Group"}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            }}
                                        </Formik>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default ModalGroupSetting;